/**
 * kitchen-print-orchestrator.ts — Orchestration des groupes KOT.
 * Lot T2, chantier 2 (tablette synchronisée).
 *
 * Deux modes, même signature de retour :
 *
 *   printGroupsDirect         — cache mémoire seul (comportement #234, zéro changement)
 *   printGroupsWithPersistence — pilotage par kot_print_jobs avec fallback direct si DB down
 *
 * La décision de quel mode utiliser appartient à PrintingApi.printKitchenTicket
 * (clé d'idempotence présente → persistence, y compris avec picker manuel — lot 4 ;
 * sinon → direct).
 *
 * Invariants :
 *   4.A.15 — aucun PII dans les logs (items = produits/quantités/notes prépa, jamais client)
 *   4.B.2  — pas d'import supabase direct (via KotPrintJobsApi)
 *   4.B.4  — 0 any
 */

import { printKitchen } from '@/services/print/PrintService';
import { logPrintAttempt } from './logging';
import { wasGroupPrinted, markGroupPrinted } from './kitchen-idempotence';
import { KotPrintJobsApi } from '@/api/kot-print-jobs/KotPrintJobsApi';
import type { KitchenSplitGroup } from './kitchen-routing';
import type { KitchenTicketPayload } from './types';

// ---------------------------------------------------------------------------
// Type de retour commun
// ---------------------------------------------------------------------------

/**
 * Résultat interne : allOk = true ssi tous les groupes traités par ce device
 * ont été imprimés avec succès (ou étaient déjà imprimés / confiés à un autre device).
 * allOk = false ssi au moins un groupe a échoué à l'impression côté serveur.
 */
export type GroupsPrintResult = {
  allOk: boolean;
};

// ---------------------------------------------------------------------------
// Helper partagé
// ---------------------------------------------------------------------------

/** Construit le payload attendu par printKitchen (format legacy). */
function buildKitchenData(
  payload: KitchenTicketPayload,
  groupItems: KitchenSplitGroup['items'],
): Record<string, unknown> {
  return {
    destination: payload.destination,
    table_number: payload.tableNumber,
    order_number: payload.orderNumber,
    server_name: payload.serverName,
    items: groupItems,
    notes: payload.notes,
    course: payload.course,
    reclame: payload.reclame,
  };
}

// ---------------------------------------------------------------------------
// Mode 1 — Direct (cache mémoire #234, comportement préservé à l'identique)
// ---------------------------------------------------------------------------

/**
 * Impression d'un groupe sans jamais lever : une exception du transport (Bluetooth
 * « Connexion BT échouée » après reconnexion) interrompait la boucle — job laissé
 * `claimed`, groupes suivants non tentés, aucun log. Elle compte désormais comme un
 * échec du groupe (markFailed + log), et les autres groupes sont imprimés.
 */
async function printGroupSafely(
  tenantId: string,
  registerId: string | undefined,
  kitchenData: ReturnType<typeof buildKitchenData>,
  printerId: string | undefined,
  printerFunction: KitchenSplitGroup['printerFunction'],
): Promise<{ success: boolean; errorMessage: string | null }> {
  try {
    // Arité legacy préservée : la fonction n'est passée que si non-null.
    const success = printerFunction
      ? await printKitchen(tenantId, registerId, kitchenData, printerId, printerFunction)
      : await printKitchen(tenantId, registerId, kitchenData, printerId);
    return { success, errorMessage: success ? null : 'impression refusée' };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn('[kot] impression groupe en exception', msg);
    return { success: false, errorMessage: msg.slice(0, 200) };
  }
}

/**
 * Imprime les groupes KOT via le cache mémoire process (#234).
 *
 * Logique extraite mot pour mot du corps de PrintingApi.printKitchenTicket avant T2 :
 *   - skip si wasGroupPrinted (idempotence locale, single-device)
 *   - printKitchen (arité legacy préservée pour printerFunction null vs non-null)
 *   - markGroupPrinted si succès
 *   - logPrintAttempt fire-and-forget
 *
 * Le picker manuel (printerId non null) court-circuite le cache : ré-imprimer
 * volontairement le même bon doit rester possible.
 *
 * @param tenantId   UUID tenant courant
 * @param groups     Groupes résolus par resolveKitchenSplit ou groupe unique legacy
 * @param payload    Payload KOT validé par kitchenTicketPayloadSchema
 * @param registerId Identifiant de caisse (résolution auto imprimante)
 * @param printerId  Identifiant imprimante explicite (picker — bypass idempotence)
 */
export async function printGroupsDirect(
  tenantId: string,
  groups: KitchenSplitGroup[],
  payload: KitchenTicketPayload,
  registerId: string | undefined,
  printerId: string | undefined,
): Promise<GroupsPrintResult> {
  let allOk = true;
  for (const group of groups) {
    // Idempotence mémoire : skip si déjà sorti lors d'une tentative précédente.
    if (!printerId && wasGroupPrinted(payload.orderNumber, group.printerFunction)) {
      continue;
    }
    const kitchenData = buildKitchenData(payload, group.items);
    const { success, errorMessage } = await printGroupSafely(
      tenantId,
      registerId,
      kitchenData,
      printerId,
      group.printerFunction,
    );
    allOk = allOk && success;
    if (success && !printerId) {
      markGroupPrinted(payload.orderNumber, group.printerFunction);
    }
    void logPrintAttempt({
      documentType: 'kitchen',
      route: 'unknown',
      success,
      errorMessage,
      registerId: registerId ?? null,
      printerId: printerId ?? null,
      durationMs: null, // durée globale suivie dans PrintingApi
    });
  }
  return { allOk };
}

// ---------------------------------------------------------------------------
// Mode 2 — Persistance DB (T2) avec fallback direct si réseau indisponible
// ---------------------------------------------------------------------------

/**
 * Imprime les groupes KOT avec idempotence persistante via kot_print_jobs.
 *
 * Flux nominal pour chaque groupe :
 *   a. Fast-path cache mémoire (wasGroupPrinted) — skip si déjà imprimé localement.
 *   b. Statut DB 'printed' → sauté + mise à jour cache mémoire.
 *   c. Claim atomique — si null, un autre device a gagné la course → skip (pas d'erreur).
 *   d. Impression → markPrinted + markGroupPrinted | markFailed.
 *
 * Dégradé réseau (insertJobsIdempotent ou listJobsByKotKey échoue) :
 *   fallback immédiat sur printGroupsDirect (console.warn sobre, jamais de blocage).
 *
 * Un claim perdu (claimJob → null) est traité comme allOk : le job sera imprimé par
 * l'autre device. allOk = false uniquement si ce device a tenté l'impression et échoué.
 *
 * @param tenantId         UUID tenant courant
 * @param groups           Groupes résolus
 * @param payload          Payload KOT validé
 * @param idempotencyKey   Clé unique de l'envoi (orderNumber ou draftId:sent_seq)
 * @param registerId       Identifiant de caisse (optionnel)
 * @param printerId        Imprimante explicite du picker (optionnel, lot 4) —
 *                         cible physique, sans désactiver l'idempotence persistante
 */
export async function printGroupsWithPersistence(
  tenantId: string,
  groups: KitchenSplitGroup[],
  payload: KitchenTicketPayload,
  idempotencyKey: string,
  registerId: string | undefined,
  printerId?: string,
): Promise<GroupsPrintResult> {
  // Étape 1 — Insertion idempotente de tous les jobs (ON CONFLICT DO NOTHING).
  const insertInputs = groups.map((g) => ({
    kot_key: idempotencyKey,
    printer_function: g.printerFunction ?? null,
    payload: g.items,
  }));

  const insertResult = await KotPrintJobsApi.insertJobsIdempotent(insertInputs);
  if (!insertResult.ok) {
    console.warn('[kot] insertJobsIdempotent échoué — fallback direct', insertResult.error.type);
    return printGroupsDirect(tenantId, groups, payload, registerId, printerId);
  }

  // Étape 2 — Lecture de tous les jobs existants pour ce kot_key (tous statuts).
  const listResult = await KotPrintJobsApi.listJobsByKotKey(idempotencyKey);
  if (!listResult.ok) {
    console.warn('[kot] listJobsByKotKey échoué — fallback direct', listResult.error.type);
    return printGroupsDirect(tenantId, groups, payload, registerId, printerId);
  }

  const existingJobs = listResult.value;
  let allOk = true;

  // Étape 3 — Traitement de chaque groupe courant.
  for (const group of groups) {
    // a. Fast-path cache mémoire : déjà imprimé localement dans cette session.
    if (wasGroupPrinted(idempotencyKey, group.printerFunction)) {
      continue;
    }

    // Recherche du job correspondant à ce groupe par printer_function.
    const printerFnKey = group.printerFunction ?? null;
    const job = existingJobs.find((j) => (j.printer_function ?? null) === printerFnKey);

    // b. Statut DB 'printed' → déjà imprimé (ce device ou un autre).
    if (job?.status === 'printed') {
      markGroupPrinted(idempotencyKey, group.printerFunction);
      continue;
    }

    // Job introuvable après l'insertion (ne devrait pas arriver) → impression directe.
    if (!job) {
      console.warn('[kot] job introuvable après insertion pour printer_function =', printerFnKey);
      const kitchenData = buildKitchenData(payload, group.items);
      const { success, errorMessage } = await printGroupSafely(
        tenantId,
        registerId,
        kitchenData,
        printerId,
        group.printerFunction,
      );
      if (success) markGroupPrinted(idempotencyKey, group.printerFunction);
      allOk = allOk && success;
      continue;
    }

    // c. Claim atomique.
    const claimResult = await KotPrintJobsApi.claimJob(job.id);
    if (!claimResult.ok) {
      // Erreur DB sur le claim → impression directe best-effort (resilience réseau).
      console.warn('[kot] claimJob erreur DB', claimResult.error.type, '— impression directe');
    } else if (claimResult.value === null) {
      // Claim perdu : un autre device imprime ce groupe → skip sans échec.
      continue;
    }

    // d. Impression physique.
    const kitchenData = buildKitchenData(payload, group.items);
    const { success, errorMessage } = await printGroupSafely(
      tenantId,
      registerId,
      kitchenData,
      printerId,
      group.printerFunction,
    );

    if (success) {
      markGroupPrinted(idempotencyKey, group.printerFunction);
      const markResult = await KotPrintJobsApi.markPrinted(job.id);
      if (!markResult.ok) {
        // L'impression physique a eu lieu — on log sans bloquer (4.A.12 best-effort côté DB).
        console.warn('[kot] markPrinted échoué pour', job.id, markResult.error.type);
      }
    } else {
      allOk = false;
      const failResult = await KotPrintJobsApi.markFailed(job.id);
      if (!failResult.ok) {
        console.warn('[kot] markFailed échoué pour', job.id, failResult.error.type);
      }
    }

    void logPrintAttempt({
      documentType: 'kitchen',
      route: 'unknown',
      success,
      errorMessage,
      registerId: registerId ?? null,
      durationMs: null,
    });
  }

  return { allOk };
}
